// =====================================================
// SALES EXECUTIVE — TERMS & CONDITIONS MODAL  (Point 11)
//
// Two modes:
//   • "gate"  — the MANDATORY acceptance flow shown on first dashboard access
//               (or after a version bump). The Accept button stays DISABLED
//               until the content is scrolled to the very end. No pre-ticked
//               checkbox, no accept-by-default. On accept it records the
//               acceptance (with device hash) and calls onAccepted().
//   • "view"  — the always-accessible read-only view (permanent "Terms &
//               Conditions" link). Just a Close button; no acceptance needed.
//
// Styling follows the existing dark-premium system (prefix `stc-`, Syne/DM Sans,
// #6332ff) so it sits naturally alongside SalesDashboard. Additive component —
// nothing else changes.
// =====================================================

import { useEffect, useRef, useState, useCallback } from "react";
import { salesTerms, errorMessage } from "../services/salesApi";
import { getDeviceFingerprint } from "../utils/deviceFingerprint";
import "./SalesTermsModal.css";

export default function SalesTermsModal({ mode = "gate", onAccepted, onClose }) {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    salesTerms
      .current()
      .then((d) => {
        if (!alive) return;
        setTerms(d.terms);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(errorMessage(err, "Could not load Terms & Conditions"));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Enable the Accept button only once the content is scrolled to the end.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 8px tolerance for sub-pixel rounding.
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      setScrolledEnd(true);
    }
  }, []);

  // If the content is short enough that there's nothing to scroll, treat it as
  // already read (otherwise the gate could never be satisfied).
  useEffect(() => {
    if (loading || mode !== "gate") return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) setScrolledEnd(true);
  }, [loading, terms, mode]);

  const accept = async () => {
    if (!scrolledEnd || accepting || !terms) return;
    setAccepting(true);
    setError("");
    try {
      const fp = await getDeviceFingerprint();
      await salesTerms.accept(terms.version, fp.deviceHash);
      if (onAccepted) onAccepted();
    } catch (err) {
      setError(errorMessage(err, "Could not record your acceptance. Please try again."));
      setAccepting(false);
    }
  };

  return (
    <div className="stc-backdrop" role="dialog" aria-modal="true">
      <div className="stc-modal">
        <div className="stc-header">
          <h2 className="stc-title">
            {terms ? terms.title : "Sales Executive — Terms & Conditions"}
          </h2>
          {terms ? <span className="stc-version">Version {terms.version}</span> : null}
        </div>

        {loading ? (
          <div className="stc-loading">Loading…</div>
        ) : (
          <>
            <div className="stc-scroll" ref={scrollRef} onScroll={onScroll}>
              {terms && terms.sections && terms.sections.length ? (
                terms.sections.map((s, i) => (
                  <section className="stc-section" key={i}>
                    {s.heading ? <h3 className="stc-section-heading">{s.heading}</h3> : null}
                    {s.body && s.body.length ? (
                      <ul className="stc-list">
                        {s.body.map((line, j) => (
                          <li key={j}>{line}</li>
                        ))}
                      </ul>
                    ) : null}
                  </section>
                ))
              ) : (
                <p className="stc-empty">No terms content is available.</p>
              )}
              <div className="stc-end-marker">— End of Terms & Conditions —</div>
            </div>

            {error ? <div className="stc-error">{error}</div> : null}

            <div className="stc-footer">
              {mode === "gate" ? (
                <>
                  {!scrolledEnd ? (
                    <span className="stc-hint">Please scroll to the end to enable the Accept button.</span>
                  ) : (
                    <span className="stc-hint stc-hint-ok">You've read to the end — you may accept now.</span>
                  )}
                  <button
                    className="stc-btn stc-btn-accept"
                    onClick={accept}
                    disabled={!scrolledEnd || accepting}
                  >
                    {accepting ? "Recording…" : "I have read and accept"}
                  </button>
                </>
              ) : (
                <button className="stc-btn stc-btn-close" onClick={onClose}>
                  Close
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
